import { Box, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import P2Blurbs from "components/UI/P2Blurbs/P2Blurbs";
import React, { useState } from "react";
import { observer } from "mobx-react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useStore } from "../../../../../utils/useStore";
import { AuthStore } from "../../../../../stores/AuthStore";
import theme from "Theme/AppTheme";
import { Blurb } from "components/UI/AcxTranscription/Components/BlurbBox/Blurb";

interface AdverseEventsSectionProps {
    store: ConversationsStore;
    setTab: React.Dispatch<React.SetStateAction<number>>;
}

const SafetyEventsSection: React.FC<AdverseEventsSectionProps> = observer(
    ({ store, setTab }) => {
        const authStore = useStore(AuthStore);
        if (!authStore.canUserView("Safety Events Model")) return null;
        const canCollapse =
            store.selectedConversation?.hasSafetyEvent.containsExamples;
        const [open, setOpen] = useState(true);

        return (
            <Box
                sx={{
                    marginTop: theme.spacing(2),
                    marginBottom: theme.spacing(2),
                }}
            >
                <Box sx={{ width: "85%" }}>
                    {canCollapse && (
                        <Tooltip
                            title={`${
                                open ? "Collapse" : "Expand"
                            } Safety Event Highlights`}
                        >
                            <IconButton
                                onClick={() => {
                                    setOpen(!open);
                                }}
                                size="small"
                                edge="start"
                                sx={{
                                    padding: 0,
                                    marginRight: theme.spacing(0.25),
                                }}
                                id={"expand-SE-highlights"}
                            >
                                {open ? (
                                    <KeyboardArrowUpIcon />
                                ) : (
                                    <KeyboardArrowDownIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                    <Typography
                        id="conversation-summary-safety-event"
                        component="span"
                        onClick={() => {
                            setOpen(!open);
                        }}
                        sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            marginBottom: theme.spacing(1),
                            ...(canCollapse && {
                                "&:hover": {
                                    cursor: "pointer",
                                },
                            }),
                            scrollMarginTop: "20px",
                        }}
                    >
                        {!store.selectedConversation?.hasSafetyEvent
                            .containsExamples
                            ? "Safety Event Signals: "
                            : "Safety Event Signals"}
                    </Typography>
                    {!store.selectedConversation?.hasSafetyEvent
                        .containsExamples && (
                        <Typography component="span">
                            No specific examples identified in transcript
                        </Typography>
                    )}
                    <Typography sx={{ fontWeight: 400, fontSize: "13px" }}>
                        {store.selectedConversation?.hasSafetyEvent
                            .containsExamples && (
                            <>
                                <Typography
                                    component="span"
                                    color={theme.palette.error[900]}
                                    fontWeight={700}
                                    sx={{}}
                                >
                                    {store.selectedConversation
                                        ?.adverseEventData.length > 1
                                        ? `${store.selectedConversation?.adverseEventData.length} Safety Event Signals `
                                        : "1 Safety Event Signal "}
                                </Typography>
                                found in this conversation
                            </>
                        )}
                    </Typography>
                </Box>

                <Collapse in={open}>
                    {store.selectedConversation?.isChat ? (
                        <P2Blurbs
                            data={
                                store.selectedConversation?.adverseEventData ??
                                []
                            }
                            chat={store.selectedConversation.chatTranscription}
                            variant="adverse"
                            speakerChannelMap={
                                store.speakerStore.speakerChannelMap
                            }
                        />
                    ) : (
                        <P2Blurbs
                            data={
                                store.selectedConversation?.adverseEventData ??
                                []
                            }
                            transcription={
                                store.selectedConversation?.audioTranscription
                                    ?.phrases ??
                                store.selectedConversation?.audioTranscription
                                    ?.words ??
                                []
                            }
                            variant="adverse"
                            speakerChannelMap={
                                store.speakerStore.speakerChannelMap
                            }
                            isPhrasesTranscript={
                                !!store.selectedConversation?.audioTranscription
                                    ?.phrases
                            }
                            onBlurbClick={(blurb) => {
                                if (blurb && "timeInterval" in blurb) {
                                    // Handle transcript blurb click
                                    const transcriptBlurb = blurb as Blurb;
                                    if (store.transcriptionStore) {
                                        const index =
                                            store.transcriptionStore.getScrollIndexForTimestamp(
                                                transcriptBlurb.timeInterval[0],
                                            );
                                        store.transcriptionStore.setScrollIndex(
                                            index,
                                        );
                                        setTab(1);
                                    }
                                }
                            }}
                        />
                    )}
                </Collapse>
            </Box>
        );
    },
);

export default SafetyEventsSection;
